import { lazy, Suspense, useEffect, useState } from 'react';

import { useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';

import Loader from '@/components/Layouts/Loader';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST } from '@/common/constants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { getListSireReviewAdmin, getListSireReviewRequestor, getListSireReviewReviewer } from '@/services/sireReviewService';
import { downloadFile } from '@/services/downloadService';
import SearchAndFilter from './SearchAndFilter';
import { getNewRecordCounterInspectingCompany, getNewRecordCounterSuperAdmin } from '@/services/newRecordCounterService';
import { setDataTotalNewSireReview } from '@/redux/slices/notificationSlice';
import { getCurrentMonth } from '@/utils/date';

import '../styles/ListPage.css'
import useSmallScreen from '@/utils/UseDeviceSize';
import apiCall from '@/services/apiCall';
import { downloadBlobResponse } from '@/services/blobDownloadService';

const TableSireReview = lazy(() => import('@/components/Tables/TableSireReview'));

const SireReviewList = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const appId = useAppSelector((state: RootState) => state.account.appId);
    const dispatch = useAppDispatch();
    const isSmallScreen = useSmallScreen();

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [dataList, setDataList] = useState([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [statusValue, setStatusValue] = useState('');
    const [typeValue, setTypeValue] = useState('');
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const {year: currentYear, month: currentMonth} = getCurrentMonth();
    const [month, setMonth] = useState<number>(currentMonth);
    const [year, setYear] = useState<number>(currentYear);

    const showToast = (error: any) => {
        toast({
            title: error?.code,
            description: error?.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true,
        });
    };

    const handleSearch = () => {
        setCurrentPageNumber(1);
        setTimeout(() => {
            getData();
        }, 300);
    };

    const handleDownload = async (id: string) => {
			await downloadFile(id);
    }

    const setLatestCounterAdmin = async () => {
        const responseCounter = await getNewRecordCounterSuperAdmin() as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewSireReview(responseCounter?.data?.sireReview || 0));
        }
    }

    // const setLatestCounterRequestor = async () => {
    //     const responseCounter = await getNewRecordCounterRequestor() as any;
    //     if (responseCounter?.status === HttpStatusCode.Ok) {
    //         dispatch(setDataTotalNewSireReview(responseCounter?.data?.sireReview || 0));
    //     }
    // }

    const setLatestCounterInspectingCompany = async () => {
        const responseCounter = await getNewRecordCounterInspectingCompany(appId) as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewSireReview(responseCounter?.data?.sireReview || 0));
        }
    }

    const getData = async () => {
        setIsLoading(true);
        const param = { search: searchValue, pageNumber: currentPageNumber, pageSize: currentPageSize, status: statusValue, type: typeValue, month, year };

        try {
            let response = {} as any;

            if (role === 'super admin') {
                response = await getListSireReviewAdmin(param) as any;
                setLatestCounterAdmin();
            } else if (role === 'requestor') {
                const newParam = { ...param, requestorId: appId };
                response = await getListSireReviewRequestor(newParam) as any;
            } else if (role === 'inspecting company') {
                const newParam = { ...param, inspectorId: appId };
                response = await getListSireReviewReviewer(newParam) as any;
                setLatestCounterInspectingCompany();
            }

            if (response?.status === HttpStatusCode.Ok) {
                setDataList(response?.data);
                setTotalPageNumber(response?.totalPage);
                setHasNextPage(response?.hasNextPage);
            } else {
                showToast(response?.error);
            }
        } catch (error: any) {
            showToast({ code: 'Error', message: 'Failed to fetch data', error });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber]);

    const handleExportToExcel = async () => {
        const query = `?search=${searchValue}&status=${statusValue}&type=${typeValue}&pageNumber=${currentPageNumber}&pageSize=${currentPageSize}&month=${month}&year=${year}`;
        const URL = `/SireReview/ExporttoExcelSireReviewAdmin${query}`;

        try {
            const response = await apiCall('get', URL, { responseType: 'blob' }) as any;
            await downloadBlobResponse(response, showToast, 'SireReview');
        } catch (error: any) {
            showToast({ code: 'Error', message: 'Failed to export data', error });
        }
    };

    // Helper to trigger loading and then getData
    const refetchWithLoading = () => {
        setIsLoading(true);
        getData();
    };

    return (
        <div className="table-container">
            <SearchAndFilter
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                statusValue={statusValue}
                setStatusValue={setStatusValue}
                typeValue={typeValue}
                setTypeValue={setTypeValue}
                onSearch={handleSearch}
                onAdd={() => navigate('/manage-sire-review/add')}
                role={role}
                month={currentMonth}
                year={currentYear}
                setMonth={setMonth}
                setYear={setYear}
                isSmallScreen={isSmallScreen}
                handleExportToExcel={handleExportToExcel}
            />
            <Suspense fallback={<Loader />}>
                <TableSireReview
                    dataList={dataList}
                    isLoading={isLoading}
                    handleReviewAdmin={(id: string) => navigate('/manage-sire-review/edit-admin', { state: { id } })}
                    handleReviewAdminReassign={(id: string) => navigate('/manage-sire-review/reassign', { state: { id } })}
                    handleReviewReviewer={(id: string) => navigate('/manage-sire-review/edit-reviewer', { state: { id } })}
                    handleReviewRequestor={(id: string) => navigate('/manage-sire-review/add', { state: { id, isReRequest: false } })}
                    handleReviewAdminRerequest={(id: string) => navigate('/manage-sire-review/add', { state: { id, isReRequest: true } })}
                    handleReviewAdminReopen={(id: string) => navigate('/manage-sire-review/reopen', { state: { id } })}
                    handleDownload={handleDownload}
                    currentPageSize={currentPageSize}
                    handleChangePageSize={setCurrentPageSize}
                    handleChangePageNumber={setCurrentPageNumber}
                    currentPageNumber={currentPageNumber}
                    totalPageNumber={totalPageNumber}
                    hasNextPage={hasNextPage}
                    role={role}
                    refetch={refetchWithLoading}
                />
            </Suspense>
        </div>
    );
};

export default SireReviewList;
